import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Modal } from '../components/ui';
import { useToast } from '../components/Toasts';
import { api, ApiRequestError } from '../lib/api';

/**
 * Changing your own password.
 *
 * The endpoint has always been there; there was no way to reach it, so the one
 * account that can reach every school on the platform kept whatever password it
 * was first given.
 *
 * Signing everyone else out is the point rather than a side effect: the reason
 * to change a password is usually that somebody else might know the old one,
 * and leaving their session alive would make the change cosmetic. The server
 * does that; this screen says so before, not after.
 */
export function ChangePasswordModal({
  onClose,
  minLength = 12,
  endpoint = '/api/auth/password',
}: {
  onClose: () => void;
  minLength?: number;
  endpoint?: string;
}) {
  const toast = useToast();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [again, setAgain] = useState('');
  const [show, setShow] = useState(false);

  const problem =
    next.length > 0 && next.length < minLength
      ? `Use at least ${minLength} characters — ${minLength - next.length} more.`
      : again.length > 0 && next !== again
        ? 'The two new passwords do not match.'
        : next.length > 0 && next === current
          ? 'That is the password you already have.'
          : null;

  const ready = current.length > 0 && next.length >= minLength && next === again && !problem;

  const save = useMutation({
    mutationFn: () => api.post(endpoint, { currentPassword: current, newPassword: next }),
    onSuccess: () => {
      toast.push('Password changed — other devices have been signed out', 'success');
      onClose();
    },
    onError: (error) =>
      toast.push(
        error instanceof ApiRequestError ? error.message : 'Could not change the password',
        'error',
      ),
  });

  return (
    <Modal
      title="Change your password"
      onClose={onClose}
      footer={
        <>
          <button className="btn" disabled={!ready || save.isPending} onClick={() => save.mutate()}>
            {save.isPending ? 'Changing…' : 'Change password'}
          </button>
          <button className="btn btn--ghost" onClick={onClose}>
            Cancel
          </button>
        </>
      }
    >
      <div className="caption">
        You will stay signed in here. Every other device signed in as you will be
        signed out.
      </div>

      <label className="field">
        <span>Current password</span>
        <input
          type={show ? 'text' : 'password'}
          value={current}
          autoComplete="current-password"
          onChange={(e) => setCurrent(e.target.value)}
        />
      </label>

      <label className="field">
        <span>New password</span>
        <input
          type={show ? 'text' : 'password'}
          value={next}
          autoComplete="new-password"
          onChange={(e) => setNext(e.target.value)}
        />
        <span className="field__hint">At least {minLength} characters.</span>
      </label>

      <label className="field">
        <span>New password again</span>
        <input
          type={show ? 'text' : 'password'}
          value={again}
          autoComplete="new-password"
          onChange={(e) => setAgain(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && ready) save.mutate();
          }}
        />
      </label>

      <label style={{ display: 'flex', alignItems: 'center', gap: 9, cursor: 'pointer' }}>
        <input type="checkbox" checked={show} onChange={(e) => setShow(e.target.checked)} />
        {/* Typing a long password blind, twice, is how people end up choosing a
            short one. */}
        <span style={{ fontSize: 13.5 }}>Show what I am typing</span>
      </label>

      {problem ? (
        <span className="field__error" style={{ fontSize: 13 }}>
          {problem}
        </span>
      ) : null}
    </Modal>
  );
}
